import { URL_ACCOUNT_TEAM } from '@seek/adv-constants';
import type { TeamMemberForJobAccess } from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Strong, Text, TextLink, Inline } from 'braid-design-system';
import { useEffect, useState } from 'react';

import {
  EVENT_NAME_MANAGE_NOTIFICATIONS_ADD_TEAM_MEMBERS_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_REQUEST_ADD_TEAM_ACCESS_PRESSED,
} from 'src/constants/pageTrackingData';
import { trackEvent } from 'src/modules/events';
import { useStores } from 'src/utils/hooks/useStores';

import translations from './.vocab';

interface Props {
  teamMembers: TeamMemberForJobAccess[];
}

export const AddTeamMembersCallout = ({ teamMembers }: Props) => {
  const { t } = useTranslations(translations);
  const {
    userStore: { user },
  } = useStores();

  const [showEmailLink, setShowEmailLink] = useState<boolean>(false);

  useEffect(() => {
    if (!user.canManageUsers) {
      setShowEmailLink(true);
    }
  }, [user, teamMembers]);

  const trackLinkInteraction = () =>
    trackEvent(
      user.canManageUsers
        ? EVENT_NAME_MANAGE_NOTIFICATIONS_ADD_TEAM_MEMBERS_PRESSED
        : EVENT_NAME_MANAGE_NOTIFICATIONS_REQUEST_ADD_TEAM_ACCESS_PRESSED,
      {},
    );

  const renderTextLink = (renderEmailLink: boolean) => {
    let linkHref = URL_ACCOUNT_TEAM;
    let linkText = t('Add team members now');
    if (renderEmailLink) {
      const adminUsers = teamMembers
        .filter((teamMember) => teamMember.isAdmin)
        .map((teamMember) => teamMember.email)
        .join(',');
      const emailSubject = t(
        'Request to add user to our SEEK Employer Account',
        { advertiserName: user.advertiserName },
      )
        .split(' ')
        .join('%20');

      linkHref = `mailto:${adminUsers}?subject=${emailSubject}`;
      linkText = t('Request access from your admin.');
    }

    return (
      <TextLink
        href={linkHref}
        target="_blank"
        rel="noopener"
        onClick={trackLinkInteraction}
      >
        {linkText}
      </TextLink>
    );
  };

  return (
    <Inline space="xsmall" collapseBelow="tablet">
      <Text>{t('Don’t see who you are looking for')}</Text>
      <Text>
        <Strong>{renderTextLink(showEmailLink)}</Strong>
      </Text>
    </Inline>
  );
};
